import { zodResolver } from '@hookform/resolvers/zod';
import { passwordSchema } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Monitor } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { TwoFactorCard } from '@/components/two-factor';
import { AppCard } from '@/components/app-card';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Input } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { SessionItem } from '@/lib/types';
import { formatDateTime, formatRelative } from '@/lib/utils';

const profileSchema = z.object({
  firstName: z.string().trim().min(1, 'Pflichtfeld').max(100),
  lastName: z.string().trim().min(1, 'Pflichtfeld').max(100),
  phone: z.string().trim().max(50),
});

const pwSchema = z
  .object({ currentPassword: z.string().min(1, 'Pflichtfeld'), newPassword: passwordSchema, confirm: z.string() })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Die Passwörter stimmen nicht überein' });

function ProfileCard() {
  const { me, refresh } = useAuth();
  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    values: { firstName: me!.user.firstName, lastName: me!.user.lastName, phone: me!.user.phone ?? '' },
  });
  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof profileSchema>) => api('/auth/profile', { method: 'PATCH', body: { ...v, phone: v.phone || null } }),
    onSuccess: async () => {
      await refresh();
      toast.success('Profil gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card>
      <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
        <CardHeader>
          <CardTitle>Persönliche Daten</CardTitle>
          <CardDescription>
            {me!.user.email}{' '}
            {me!.user.emailVerifiedAt ? <Badge variant="success">bestätigt</Badge> : <Badge variant="warning">nicht bestätigt</Badge>}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Vorname" htmlFor="firstName" error={form.formState.errors.firstName?.message}>
            <Input id="firstName" {...form.register('firstName')} />
          </Field>
          <Field label="Nachname" htmlFor="lastName" error={form.formState.errors.lastName?.message}>
            <Input id="lastName" {...form.register('lastName')} />
          </Field>
          <Field label="Telefon (Handy)" htmlFor="phone" className="sm:col-span-2" hint="Für WhatsApp-Benachrichtigungen aus Automatisierungen, z. B. +49 151 1234567">
            <Input id="phone" type="tel" {...form.register('phone')} />
          </Field>
          <div className="sm:col-span-2">
            <p className="mb-1.5 text-sm font-medium">Rollen</p>
            <div className="flex flex-wrap gap-1.5">
              {me!.user.roles.map((r) => (
                <Badge key={r.id} variant="secondary">
                  {r.name}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" loading={mutation.isPending} disabled={!form.formState.isDirty}>
            Speichern
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

export function PasswordCard() {
  const qc = useQueryClient();
  const form = useForm<z.infer<typeof pwSchema>>({ resolver: zodResolver(pwSchema), defaultValues: { currentPassword: '', newPassword: '', confirm: '' } });
  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof pwSchema>) =>
      api('/auth/password/change', { method: 'POST', body: { currentPassword: v.currentPassword, newPassword: v.newPassword } }),
    onSuccess: () => {
      form.reset();
      qc.invalidateQueries({ queryKey: ['sessions'] });
      toast.success('Passwort geändert. Andere Sitzungen wurden beendet.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card>
      <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
        <CardHeader>
          <CardTitle>Passwort ändern</CardTitle>
          <CardDescription>Alle anderen angemeldeten Geräte werden dabei abgemeldet.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Field label="Aktuelles Passwort" htmlFor="currentPassword" error={form.formState.errors.currentPassword?.message}>
            <Input id="currentPassword" type="password" autoComplete="current-password" {...form.register('currentPassword')} />
          </Field>
          <Field label="Neues Passwort" htmlFor="newPassword" error={form.formState.errors.newPassword?.message}>
            <Input id="newPassword" type="password" autoComplete="new-password" {...form.register('newPassword')} />
          </Field>
          <Field label="Neues Passwort wiederholen" htmlFor="confirm" error={form.formState.errors.confirm?.message}>
            <Input id="confirm" type="password" autoComplete="new-password" {...form.register('confirm')} />
          </Field>
        </CardContent>
        <CardFooter>
          <Button type="submit" loading={mutation.isPending}>
            Passwort ändern
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function describeAgent(ua: string | null) {
  if (!ua) return 'Unbekanntes Gerät';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return [browser, os].filter(Boolean).join(' auf ');
}

function SessionsCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['sessions'], queryFn: () => api<SessionItem[]>('/auth/sessions') });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      toast.success('Sitzung beendet');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle>Angemeldete Geräte</CardTitle>
        <CardDescription>Beenden Sie Sitzungen, die Sie nicht kennen.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {data?.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="flex items-center gap-3">
                <Monitor className="size-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {describeAgent(s.userAgent)} {s.current && <Badge className="ml-1">Diese Sitzung</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.ipAddress ?? '–'} · angemeldet {formatDateTime(s.createdAt)} · aktiv {formatRelative(s.lastSeenAt)}
                  </p>
                </div>
              </div>
              {!s.current && (
                <Button variant="outline" size="sm" onClick={() => revoke.mutate(s.id)} loading={revoke.isPending && revoke.variables === s.id}>
                  Beenden
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function ProfilePage() {
  return (
    <>
      <PageHeader title="Mein Profil" description="Persönliche Daten, Passwort und Sitzungen" />
      <div className="grid gap-4 lg:grid-cols-2">
        <ProfileCard />
        <PasswordCard />
        <TwoFactorCard />
        <AppCard />
        <SessionsCard />
      </div>
    </>
  );
}
